import { useEffect, useRef, useState } from 'react';
import type { SuggestedVerse } from '@/lib/verse-suggest';
import { ensureBibleHydrated } from '@/lib/bible/active';
import { resolveTopicVerseText } from '@/lib/bible-search/topics';
import styles from './ScriptureVersesSheet.module.css';

/**
 * Read-only render of a prayer's SAVED curated verses (rule #80, the
 * verse lane). Re-homed out of the deleted SpineCompanionsSheet. Verses
 * are searched only in the share-flow Find fork and the Garden card's
 * "Find" action (Chunk 2); this list only READS what a search already
 * saved onto the prayer. Server order
 * preserved (Jesus-words entries first, then descending fit). Short
 * entries (<= 3 verses) show the full BSB text; chapter-scale entries
 * show the operator context sentence + a one-verse excerpt. NO reader
 * deep-link (the Bible reader is dormant); verse TEXT resolves from
 * local BSB at render (rule #62/#76 single text source, a public asset
 * — no prayer data crosses the wire).
 */
export function ScriptureVerseList({ verses }: { verses: SuggestedVerse[] }) {
  return (
    <ul className={styles.verseList}>
      {verses.map((v) => (
        <VerseCard key={`${v.category}|${v.ref}`} v={v} />
      ))}
    </ul>
  );
}

function VerseCard({ v }: { v: SuggestedVerse }) {
  const [text, setText] = useState<string | null>(null);
  const mountedRef = useRef(true);

  // Which verse anchors the chapter-scale excerpt: the first red-letter
  // verse when the entry carries Jesus' words, else the range start; a
  // whole-chapter entry with no wj verse shows no excerpt.
  const anchor = v.wjVerses[0] ?? v.segments?.[0]?.[0] ?? null;

  useEffect(() => {
    mountedRef.current = true;
    const want: [number, number][] =
      v.scale === 'short' && v.segments
        ? v.segments
        : anchor != null
          ? [[anchor, anchor]]
          : [];
    if (want.length === 0) return;
    ensureBibleHydrated()
      .then(() =>
        Promise.all(
          want.map(([a, b]) =>
            resolveTopicVerseText({
              bookSlug: v.bookSlug,
              chapter: v.chapter,
              verseStart: a,
              verseEnd: b,
            }),
          ),
        ),
      )
      .then((parts) => {
        if (mountedRef.current) setText(parts.filter(Boolean).join(' … '));
      })
      .catch(() => {
        if (mountedRef.current) setText('');
      });
    return () => {
      mountedRef.current = false;
    };
  }, [v, anchor]);

  return (
    <li className={styles.verseCard}>
      <p className={styles.verseContext}>{v.context}</p>
      <div className={styles.pivot}>
        <span className={styles.pivotRef}>{`✦ ${v.ref}`}</span>
        {text ? <span className={styles.pivotText}>{text}</span> : null}
      </div>
    </li>
  );
}
